import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GameState, PieceType } from "@/lib/chess-engine";
import { modernPieceImages } from "@/lib/pieces";

interface CapturedPiecesProps {
  gameState: GameState;
  playerColor: 'white' | 'black';
}

export function CapturedPieces({ gameState, playerColor }: CapturedPiecesProps) {
  const calculateCapturedPieces = () => {
    // Initial piece counts for both sides
    const initialCounts = {
      white: {
        P: 8, R: 2, N: 2, B: 2, Q: 1, K: 1
      },
      black: {
        P: 8, R: 2, N: 2, B: 2, Q: 1, K: 1
      }
    };

    // Count current pieces on the board
    const currentCounts = {
      white: {
        P: 0, R: 0, N: 0, B: 0, Q: 0, K: 0
      },
      black: {
        P: 0, R: 0, N: 0, B: 0, Q: 0, K: 0
      }
    };

    // Count pieces currently on board
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = gameState.board[row][col];
        if (piece) {
          currentCounts[piece.color][piece.type]++;
        }
      }
    }

    // Calculate captured pieces (initial - current)
    const captured = {
      white: {} as Record<PieceType, number>,
      black: {} as Record<PieceType, number>
    };

    (['P', 'N', 'B', 'R', 'Q', 'K'] as PieceType[]).forEach(pieceType => {
      captured.white[pieceType] = initialCounts.white[pieceType] - currentCounts.white[pieceType];
      captured.black[pieceType] = initialCounts.black[pieceType] - currentCounts.black[pieceType];
    });

    return captured;
  };

  const capturedPieces = calculateCapturedPieces();
  
  // Get captured pieces for display (opponent's captured pieces = pieces we captured)
  const myCapturedPieces = capturedPieces[playerColor === 'white' ? 'black' : 'white'];
  const opponentCapturedPieces = capturedPieces[playerColor];

  const renderCapturedPiecesRow = (pieces: Record<PieceType, number>, color: 'white' | 'black') => {
    const capturedList: { type: PieceType; count: number }[] = [];
    
    (['P', 'N', 'B', 'R', 'Q'] as PieceType[]).forEach(pieceType => {
      if (pieces[pieceType] > 0) {
        capturedList.push({ type: pieceType, count: pieces[pieceType] });
      }
    });

    if (capturedList.length === 0) {
      return <div className="text-xs text-muted-foreground">No captures</div>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {capturedList.map(({ type, count }) => {
          const pieceKey = color === 'white' ? type : type.toLowerCase();
          const pieceImage = modernPieceImages[pieceKey];
          return (
            <div key={type} className="flex items-center">
              {Array.from({ length: count }).map((_, index) => (
                <img
                  key={index}
                  src={pieceImage}
                  alt={`${color} ${type}`}
                  className="w-6 h-6"
                  draggable={false}
                />
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  // Calculate material advantage
  const pieceValues = { P: 1, N: 3, B: 3, R: 5, Q: 9, K: 0 };
  const myAdvantage = (['P', 'N', 'B', 'R', 'Q'] as PieceType[]).reduce((sum, type) => {
    return sum + (myCapturedPieces[type] * pieceValues[type]) - (opponentCapturedPieces[type] * pieceValues[type]);
  }, 0);

  return (
    <Card className="border-accent/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Captured Pieces</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="text-xs font-medium mb-1">You captured:</div>
          {renderCapturedPiecesRow(myCapturedPieces, playerColor === 'white' ? 'black' : 'white')}
        </div>
        
        <div>
          <div className="text-xs font-medium mb-1">Opponent captured:</div>
          {renderCapturedPiecesRow(opponentCapturedPieces, playerColor)}
        </div>

        {myAdvantage !== 0 && (
          <div className={`text-xs font-medium ${myAdvantage > 0 ? 'text-success' : 'text-destructive'}`}>
            Material: {myAdvantage > 0 ? '+' : ''}{myAdvantage}
          </div>
        )}
      </CardContent>
    </Card>
  );
}